import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getPublicMenu } from '../lib/api';
import { Input } from '../components/ui/input';
import { Search, Box, ChefHat, Star, Globe, Play, Plus, ShoppingCart } from 'lucide-react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';

export default function PublicMenu() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { t, lang, setLang, rtl } = useLanguage();
    const { addItem, totalItems } = useCart();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');

    useEffect(() => {
        const fetchMenu = async () => {
            try {
                const response = await getPublicMenu(slug);
                setData(response.data);
            } catch (err) {
                setError(err.response?.status === 404 ? 'Menu not found' : 'Failed to load menu');
            } finally {
                setLoading(false);
            }
        };
        fetchMenu();
    }, [slug]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#0D0D0D]">
                <motion.div
                    className="flex flex-col items-center gap-5"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.6 }}
                >
                    <div className="relative w-14 h-14">
                        <div className="absolute inset-0 border-2 border-[#C9A227]/20 rounded-full" />
                        <div className="absolute inset-0 border-2 border-[#C9A227] border-t-transparent rounded-full animate-spin" />
                    </div>
                    <p className="text-[#C9A227]/70 text-xs tracking-[0.2em] uppercase">{t('loading')}</p>
                </motion.div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#0D0D0D]">
                <motion.div
                    className="text-center px-6"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                >
                    <ChefHat className="w-20 h-20 text-[#C9A227]/20 mx-auto mb-6" />
                    <h1 className="text-xl font-light text-white/80 mb-3 tracking-tight">{t('not_found')}</h1>
                    <p className="text-white/30 text-sm">{t('not_found_desc')}</p>
                </motion.div>
            </div>
        );
    }

    const { restaurant, categories, dishes } = data;
    const primaryColor = restaurant.primary_color || '#C9A227';
    const bgColor = restaurant.background_color || '#0D0D0D';
    const fontFamily = restaurant.font_family || 'Playfair Display';
    const cardStyle = restaurant.card_style || 'glass';
    const animStyle = restaurant.animation_style || 'fade';

    // Animation variants based on theme setting
    const dishVariants = {
        fade: { initial: { opacity: 0 }, animate: { opacity: 1 } },
        slide: { initial: { opacity: 0, y: 30 }, animate: { opacity: 1, y: 0 } },
        scale: { initial: { opacity: 0, scale: 0.92 }, animate: { opacity: 1, scale: 1 } },
    };
    const selectedVariant = dishVariants[animStyle] || dishVariants.fade;

    const filteredDishes = dishes.filter(dish => {
        const matchesSearch = dish.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                             dish.description?.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesCategory = selectedCategory === 'all' || dish.category_id === selectedCategory;
        return matchesSearch && matchesCategory;
    });

    const groupedDishes = categories.reduce((acc, cat) => {
        const catDishes = filteredDishes.filter(d => d.category_id === cat.id);
        if (catDishes.length > 0) acc.push({ ...cat, dishes: catDishes });
        return acc;
    }, []);

    const isDark = ['#0d0d0d', '#1a1a1a', '#000000'].includes(bgColor.toLowerCase());

    return (
        <div
            className="min-h-screen pb-20"
            style={{ backgroundColor: bgColor, '--primary': primaryColor, fontFamily: `'${fontFamily}', sans-serif` }}
            dir={rtl ? 'rtl' : 'ltr'}
        >
            {/* Dynamic Google Font */}
            <link rel="stylesheet" href={`https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontFamily)}:wght@300;400;500;600;700&display=swap`} />
            {/* Language Toggle */}
            <motion.div
                className="fixed top-4 right-4 z-50"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
            >
                <button
                    onClick={() => setLang(lang === 'en' ? 'ar' : 'en')}
                    className="w-10 h-10 rounded-full flex items-center justify-center backdrop-blur-2xl transition-all active:scale-90"
                    style={{ backgroundColor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.06)' }}
                    data-testid="language-toggle"
                >
                    <Globe className="w-4 h-4" style={{ color: isDark ? 'rgba(255,255,255,0.7)' : '#333' }} />
                </button>
            </motion.div>

            {/* Floating Cart */}
            {totalItems > 0 && (
                <motion.button
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    onClick={() => navigate(`/menu/${slug}/cart`)}
                    className="fixed bottom-20 right-5 z-50 w-14 h-14 rounded-full flex items-center justify-center shadow-2xl active:scale-90 transition-all"
                    style={{ backgroundColor: primaryColor, boxShadow: `0 8px 30px ${primaryColor}40` }}
                    data-testid="floating-cart-menu"
                >
                    <ShoppingCart className="w-5 h-5 text-white" />
                    <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-white text-[10px] font-bold flex items-center justify-center" style={{ color: primaryColor }}>
                        {totalItems}
                    </span>
                </motion.button>
            )}

            {/* Hero Header */}
            <header className="relative pt-safe">
                <div
                    className="relative px-6 pt-14 pb-10"
                    style={{
                        background: isDark
                            ? `radial-gradient(ellipse at top, ${primaryColor}12 0%, transparent 60%)`
                            : `radial-gradient(ellipse at top, ${primaryColor}08 0%, transparent 60%)`
                    }}
                >
                    <div className="max-w-lg mx-auto text-center relative">
                        {/* Logo */}
                        <motion.div
                            initial={{ scale: 0.7, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                            className="mb-5"
                        >
                            {restaurant.logo_url ? (
                                <img
                                    src={restaurant.logo_url}
                                    alt={restaurant.name}
                                    className="w-20 h-20 object-contain mx-auto rounded-2xl"
                                    style={{ boxShadow: `0 12px 40px ${primaryColor}25` }}
                                />
                            ) : (
                                <div
                                    className="w-20 h-20 rounded-2xl mx-auto flex items-center justify-center"
                                    style={{
                                        backgroundColor: primaryColor,
                                        boxShadow: `0 12px 40px ${primaryColor}35`,
                                    }}
                                >
                                    <ChefHat className="w-10 h-10 text-white" />
                                </div>
                            )}
                        </motion.div>

                        {/* Restaurant name */}
                        <motion.h1
                            initial={{ y: 24, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.15, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                            className="text-3xl md:text-4xl font-light tracking-tight mb-2"
                            style={{ color: isDark ? '#fff' : '#1a1a1a' }}
                        >
                            {restaurant.name}
                        </motion.h1>

                        {restaurant.description && (
                            <motion.p
                                initial={{ y: 20, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                transition={{ delay: 0.25, duration: 0.5 }}
                                className="text-sm"
                                style={{ color: isDark ? 'rgba(255,255,255,0.4)' : '#888' }}
                            >
                                {restaurant.description}
                            </motion.p>
                        )}

                        {/* Stars */}
                        <motion.div
                            initial={{ y: 20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.35 }}
                            className="flex items-center justify-center gap-1.5 mt-4"
                        >
                            {[1, 2, 3, 4, 5].map((star) => (
                                <Star key={star} className="w-3.5 h-3.5" style={{ color: primaryColor, fill: primaryColor }} />
                            ))}
                        </motion.div>
                    </div>
                </div>

                {/* Search */}
                <div className="px-6 -mt-1">
                    <div className="max-w-lg mx-auto">
                        <motion.div
                            initial={{ y: 20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.4 }}
                            className="relative"
                        >
                            <Search
                                className="absolute left-4 top-1/2 -translate-y-1/2 w-4.5 h-4.5"
                                style={{ color: isDark ? '#444' : '#bbb' }}
                            />
                            <Input
                                placeholder={t('search')}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-12 pr-4 py-6 rounded-2xl border-0 text-sm"
                                style={{
                                    backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : '#f5f5f5',
                                    color: isDark ? '#fff' : '#1a1a1a',
                                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#eee'}`,
                                }}
                                data-testid="menu-search-input"
                            />
                        </motion.div>
                    </div>
                </div>
            </header>

            {/* Category Pills */}
            <div className="sticky top-0 z-40 py-4 px-4 overflow-x-auto scrollbar-hide" style={{ backgroundColor: bgColor }}>
                <motion.div
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.5 }}
                    className="max-w-lg mx-auto flex gap-2 pb-1"
                >
                    <button
                        onClick={() => setSelectedCategory('all')}
                        className="px-5 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap"
                        style={selectedCategory === 'all'
                            ? { backgroundColor: primaryColor, color: '#fff', boxShadow: `0 4px 16px ${primaryColor}30` }
                            : { backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : '#f0f0f0', color: isDark ? '#666' : '#888', border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#e5e5e5'}` }
                        }
                        data-testid="category-all"
                    >
                        {t('all')}
                    </button>
                    {categories.map((cat) => (
                        <button
                            key={cat.id}
                            onClick={() => setSelectedCategory(cat.id)}
                            className="px-5 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap"
                            style={selectedCategory === cat.id
                                ? { backgroundColor: primaryColor, color: '#fff', boxShadow: `0 4px 16px ${primaryColor}30` }
                                : { backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : '#f0f0f0', color: isDark ? '#666' : '#888', border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#e5e5e5'}` }
                            }
                            data-testid={`category-${cat.id}`}
                        >
                            {cat.name}
                        </button>
                    ))}
                </motion.div>
            </div>

            {/* Menu Items */}
            <main className="px-4 py-6 max-w-lg mx-auto">
                {groupedDishes.length === 0 ? (
                    <div className="text-center py-20">
                        <ChefHat className="w-16 h-16 mx-auto mb-4" style={{ color: `${primaryColor}25` }} />
                        <p style={{ color: isDark ? '#444' : '#bbb' }}>{t('no_dishes')}</p>
                    </div>
                ) : (
                    <div className="space-y-10">
                        {groupedDishes.map((category, catIndex) => (
                            <motion.section
                                key={category.id}
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.6 + catIndex * 0.12, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                            >
                                {/* Category header */}
                                <div className="flex items-center gap-3 mb-5">
                                    <div
                                        className="w-0.5 h-5 rounded-full"
                                        style={{ backgroundColor: primaryColor }}
                                    />
                                    <h2
                                        className="text-lg font-light tracking-tight"
                                        style={{ color: isDark ? 'rgba(255,255,255,0.8)' : '#222' }}
                                    >
                                        {category.name}
                                    </h2>
                                </div>

                                {/* Dish cards */}
                                <div className="space-y-3">
                                    {category.dishes.map((dish, dishIndex) => (
                                        <motion.div
                                            key={dish.id}
                                            initial={selectedVariant.initial}
                                            animate={selectedVariant.animate}
                                            transition={{ delay: 0.7 + catIndex * 0.1 + dishIndex * 0.06, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                                        >
                                            <Link
                                                to={`/menu/${slug}/dish/${dish.id}`}
                                                className="block rounded-2xl overflow-hidden transition-transform active:scale-[0.98]"
                                                style={{
                                                    backgroundColor: cardStyle === 'glass'
                                                        ? (isDark ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.7)')
                                                        : (isDark ? 'rgba(255,255,255,0.06)' : '#fff'),
                                                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'}`,
                                                    backdropFilter: cardStyle === 'glass' ? 'blur(12px)' : 'none',
                                                }}
                                                data-testid={`dish-card-${dish.id}`}
                                            >
                                                <div className="flex">
                                                    {/* Thumbnail */}
                                                    <div className="w-[108px] h-[108px] flex-shrink-0 relative overflow-hidden">
                                                        {dish.image_url ? (
                                                            <img
                                                                src={dish.image_url}
                                                                alt={dish.name}
                                                                className="w-full h-full object-cover"
                                                                loading="lazy"
                                                            />
                                                        ) : (
                                                            <div
                                                                className="w-full h-full flex items-center justify-center"
                                                                style={{ backgroundColor: `${primaryColor}08` }}
                                                            >
                                                                <ChefHat className="w-7 h-7" style={{ color: `${primaryColor}30` }} />
                                                            </div>
                                                        )}
                                                        {/* Badges */}
                                                        <div className="absolute bottom-1.5 left-1.5 flex gap-1">
                                                            {dish.video_url && (
                                                                <div className="px-1.5 py-0.5 rounded-full text-[9px] font-semibold flex items-center gap-0.5" style={{ backgroundColor: 'rgba(147,51,234,0.85)', color: '#fff' }}>
                                                                    <Play className="w-2.5 h-2.5" />
                                                                </div>
                                                            )}
                                                            {dish.model_url && (
                                                                <div className="px-1.5 py-0.5 rounded-full text-[9px] font-semibold flex items-center gap-0.5" style={{ backgroundColor: primaryColor, color: '#fff' }}>
                                                                    <Box className="w-2.5 h-2.5" /> AR
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Content */}
                                                    <div className="flex-1 px-4 py-3.5 flex flex-col justify-center">
                                                        <h3
                                                            className="font-medium text-[15px] mb-1 leading-snug"
                                                            style={{ color: isDark ? '#fff' : '#1a1a1a' }}
                                                        >
                                                            {dish.name}
                                                        </h3>
                                                        {dish.description && (
                                                            <p
                                                                className="text-[12px] line-clamp-2 mb-2 leading-relaxed"
                                                                style={{ color: isDark ? 'rgba(255,255,255,0.3)' : '#aaa' }}
                                                            >
                                                                {dish.description}
                                                            </p>
                                                        )}
                                                        <div className="flex items-center justify-between">
                                                            <p
                                                                className="font-semibold text-base"
                                                                style={{ color: primaryColor }}
                                                            >
                                                                QAR {dish.price.toFixed(0)}
                                                            </p>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    e.stopPropagation();
                                                                    addItem(dish, slug);
                                                                    toast.success(`${dish.name} added!`);
                                                                }}
                                                                className="w-8 h-8 rounded-full flex items-center justify-center transition-all active:scale-90"
                                                                style={{ backgroundColor: `${primaryColor}15`, color: primaryColor }}
                                                                data-testid={`add-to-cart-${dish.id}`}
                                                            >
                                                                <Plus className="w-4 h-4" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </Link>
                                        </motion.div>
                                    ))}
                                </div>
                            </motion.section>
                        ))}
                    </div>
                )}
            </main>

            {/* Footer */}
            <footer
                className="fixed bottom-0 left-0 right-0 text-center py-3 text-[10px] tracking-[0.1em] backdrop-blur-2xl"
                style={{
                    backgroundColor: isDark ? 'rgba(13,13,13,0.92)' : 'rgba(255,255,255,0.92)',
                    color: isDark ? '#333' : '#ccc',
                    borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.04)' : '#f0f0f0'}`,
                }}
            >
                Made with <span style={{ color: primaryColor }}>Scano</span>
            </footer>
        </div>
    );
}
